/**
 * Per-login cloud sync (offline-first).
 *
 * localStorage stays the working copy so the app works with no network.
 * This engine mirrors a few keys to the Supabase `user_data` table and pulls
 * changes made on other computers. It never compares clocks: per key it
 * remembers the server `updated_at` it last saw (`syncedAt`) and whether the
 * key was edited locally since (`dirty`).
 *
 * When two computers have both edited a list, the lists are merged. To tell
 * "added elsewhere" from "deleted here", it also remembers which items were in
 * the list the last time this computer and the cloud agreed (the "base"): an
 * item that was in the base but is missing from either side was deleted there,
 * so it is not brought back.
 */
import { supabase as defaultClient } from './supabase';

const META_KEY = 'sleekHearSyncMeta';
const BASE_KEY = 'sleekHearSyncBase';
const OWNER_KEY = 'sleekHearOwner';
const PUSH_DELAY_MS = 1500;
const POLL_MS = 60000;
const MAX_BYTES = 900 * 1024; // table check is 1 MB; stay under it

// Settings that belong to a computer, not to the login.
export const DEVICE_ONLY_SETTINGS = ['audioGain', 'darkMode'];

const norm = (s) => String(s || '').toLowerCase().trim();

const asArray = (v) => (Array.isArray(v) ? v : []);

// Union by key; items from `local` win on a clash, remote-only items are appended.
// `base` (a Set of keys from the last agreed state, or null) lets it honour
// deletions: a key in the base that either side no longer has stays deleted.
const unionBy = (keyOf) => (local, remote, base) => {
  const localList = asArray(local);
  const remoteList = asArray(remote);
  const inLocal = new Set(localList.map(keyOf));
  const inRemote = new Set(remoteList.map(keyOf));
  const deleted = (k) => !!base && base.has(k) && (!inLocal.has(k) || !inRemote.has(k));
  const out = localList.filter((item) => !deleted(keyOf(item)));
  const seen = new Set(out.map(keyOf));
  remoteList.forEach((item) => {
    const k = keyOf(item);
    if (!seen.has(k) && !deleted(k)) { seen.add(k); out.push(item); }
  });
  return out;
};

// Plans are merged by id; for the same plan the most recently updated wins.
// A plan deleted on one side (it is in the base but missing there) stays deleted.
const mergePlans = (local, remote, base) => {
  const inLocal = new Set(asArray(local).map((p) => p.id));
  const inRemote = new Set(asArray(remote).map((p) => p.id));
  const deleted = (id) => !!base && base.has(id) && (!inLocal.has(id) || !inRemote.has(id));
  const out = asArray(local).filter((p) => !deleted(p.id));
  const index = new Map(out.map((p, i) => [p.id, i]));
  asArray(remote).filter((p) => !deleted(p.id)).forEach((plan) => {
    if (!index.has(plan.id)) {
      index.set(plan.id, out.length);
      out.push(plan);
    } else if ((plan.updatedAt || 0) > (out[index.get(plan.id)].updatedAt || 0)) {
      out[index.get(plan.id)] = plan;
    }
  });
  return out;
};

const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

export const SYNC_KEYS = {
  settings: {
    storageKey: 'sleekHearSettings',
    valid: isPlainObject,
    toRemote: (settings) => {
      const copy = { ...settings };
      DEVICE_ONLY_SETTINGS.forEach((k) => delete copy[k]);
      return copy;
    },
    fromRemote: (remote, local) => {
      const merged = { ...remote };
      DEVICE_ONLY_SETTINGS.forEach((k) => {
        if (local && k in local) merged[k] = local[k];
      });
      return merged;
    },
    merge: (local) => local,
  },
  verseLibrary: {
    storageKey: 'sleekHearVerseLibrary',
    valid: Array.isArray,
    identity: (v) => norm(v && v.reference),
    merge: unionBy((v) => norm(v && v.reference)),
  },
  songLibrary: {
    storageKey: 'sleekHearSongLibrary',
    valid: Array.isArray,
    identity: (s) => norm(s && s.title),
    merge: unionBy((s) => norm(s && s.title)),
  },
  songUsage: {
    storageKey: 'sleekHearSongUsage',
    valid: Array.isArray,
    identity: (e) => e && e.id,
    merge: unionBy((e) => e && e.id),
  },
  plans: {
    storageKey: 'sleekHearPlans',
    valid: (v) => Array.isArray(v) && v.every((p) => p && typeof p.id === 'string' && Array.isArray(p.items)),
    identity: (p) => p.id,
    merge: mergePlans,
  },
};

const KEYS = Object.keys(SYNC_KEYS);

export class SyncService {
  constructor({ client, storage } = {}) {
    this._client = client;
    this._storage = storage;
    this.userId = null;
    this.status = 'local'; // local | syncing | synced | offline | error
    this.error = '';
    this.versions = {};
    this.flushing = false;
    this.flushAgain = false;
    this.pushTimer = null;
    this.pollTimer = null;
    this.handlers = [];
    this.statusListeners = new Set();
    this.appliedListeners = new Set();
  }

  get client() { return this._client || defaultClient; }
  get storage() { return this._storage || window.localStorage; }
  get online() { return typeof navigator === 'undefined' || navigator.onLine !== false; }

  // ---- local storage helpers ----

  readLocal(key) {
    try {
      const raw = this.storage.getItem(SYNC_KEYS[key].storageKey);
      if (raw === null || raw === undefined) return null;
      const value = JSON.parse(raw);
      return SYNC_KEYS[key].valid(value) ? value : null;
    } catch {
      return null;
    }
  }

  writeLocal(key, value) {
    try {
      this.storage.setItem(SYNC_KEYS[key].storageKey, JSON.stringify(value));
    } catch {
      // storage full/unavailable — nothing more we can do locally
    }
  }

  getMeta() {
    try {
      const meta = JSON.parse(this.storage.getItem(META_KEY) || '{}');
      return isPlainObject(meta) ? meta : {};
    } catch {
      return {};
    }
  }

  saveMeta(meta) {
    try {
      this.storage.setItem(META_KEY, JSON.stringify(meta));
    } catch {
      // ignore
    }
  }

  // The item identities both sides last agreed on, per key (see the header note).
  getBase() {
    try {
      const base = JSON.parse(this.storage.getItem(BASE_KEY) || '{}');
      return isPlainObject(base) ? base : {};
    } catch {
      return {};
    }
  }

  setBase(key, value) {
    const cfg = SYNC_KEYS[key];
    if (!cfg.identity || !Array.isArray(value)) return;
    try {
      const base = this.getBase();
      base[key] = value.map(cfg.identity);
      this.storage.setItem(BASE_KEY, JSON.stringify(base));
    } catch {
      // ignore: without a base the merge just can't honour deletions
    }
  }

  baseSet(key) {
    const ids = this.getBase()[key];
    return Array.isArray(ids) ? new Set(ids) : null;
  }

  // ---- status / subscriptions ----

  setStatus(status, error = '') {
    this.status = status;
    this.error = error;
    this.statusListeners.forEach((fn) => fn({ status, error }));
  }

  onStatus(fn) {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  onApplied(fn) {
    this.appliedListeners.add(fn);
    return () => this.appliedListeners.delete(fn);
  }

  emitApplied(key, value) {
    this.appliedListeners.forEach((fn) => fn(key, value));
  }

  fail(error) {
    const message = (error && error.message) || String(error);
    if (!this.online || /failed to fetch|network|load failed/i.test(message)) {
      this.setStatus('offline');
    } else {
      this.setStatus('error', message);
    }
  }

  // ---- lifecycle ----

  /**
   * Synchronous, no network. If a different login last used this browser,
   * its synced data is cleared so it can never be uploaded into this account.
   * Data from before sync existed (no owner yet) is adopted by this login.
   */
  prepare(userId) {
    if (!userId) return false;
    let cleared = false;
    try {
      const owner = this.storage.getItem(OWNER_KEY);
      if (owner && owner !== userId) {
        KEYS.forEach((k) => this.storage.removeItem(SYNC_KEYS[k].storageKey));
        this.storage.removeItem(META_KEY);
        this.storage.removeItem(BASE_KEY);
        cleared = true;
      }
      this.storage.setItem(OWNER_KEY, userId);
    } catch {
      // ignore
    }
    return cleared;
  }

  async start(userId) {
    this.stop();
    if (!userId || !this.client) {
      this.setStatus('local');
      return;
    }
    this.userId = userId;
    this.prepare(userId);

    if (typeof window !== 'undefined') {
      const pull = () => this.pullAll();
      const goOffline = () => this.setStatus('offline');
      const onVisible = () => { if (document.visibilityState === 'visible') pull(); };
      window.addEventListener('focus', pull);
      window.addEventListener('online', pull);
      window.addEventListener('offline', goOffline);
      document.addEventListener('visibilitychange', onVisible);
      this.handlers = [
        () => window.removeEventListener('focus', pull),
        () => window.removeEventListener('online', pull),
        () => window.removeEventListener('offline', goOffline),
        () => document.removeEventListener('visibilitychange', onVisible),
      ];
      this.pollTimer = setInterval(() => {
        if (document.visibilityState === 'visible') pull();
      }, POLL_MS);
    }
    await this.pullAll();
  }

  stop() {
    clearTimeout(this.pushTimer);
    clearInterval(this.pollTimer);
    this.pushTimer = null;
    this.pollTimer = null;
    this.handlers.forEach((off) => off());
    this.handlers = [];
    this.userId = null;
    this.setStatus('local');
  }

  canSync() {
    return !!this.client && !!this.userId;
  }

  // ---- local edits ----

  /** Called by useSyncedStorage after a real local change. */
  markDirty(key) {
    if (!SYNC_KEYS[key]) return;
    this.versions[key] = (this.versions[key] || 0) + 1;
    const meta = this.getMeta();
    meta[key] = { ...(meta[key] || {}), dirty: true };
    this.saveMeta(meta);
    if (!this.canSync()) return;
    clearTimeout(this.pushTimer);
    this.pushTimer = setTimeout(() => this.flush(), PUSH_DELAY_MS);
  }

  // ---- push ----

  async push(key) {
    const cfg = SYNC_KEYS[key];
    const local = this.readLocal(key);
    if (local === null) {
      const meta = this.getMeta();
      if (meta[key]) { meta[key].dirty = false; this.saveMeta(meta); }
      return true;
    }
    const payload = cfg.toRemote ? cfg.toRemote(local) : local;
    if (JSON.stringify(payload).length > MAX_BYTES) {
      this.setStatus('error', `${key} is too large to sync`);
      return false;
    }
    const version = this.versions[key] || 0;
    const { data, error } = await this.client
      .from('user_data')
      .upsert({ user_id: this.userId, key, value: payload }, { onConflict: 'user_id,key' })
      .select('updated_at')
      .single();
    if (error) {
      this.fail(error);
      return false;
    }
    this.setBase(key, local); // the cloud now holds exactly what was pushed
    const meta = this.getMeta();
    // If it changed again while this push was in flight, it is still dirty.
    meta[key] = { syncedAt: data.updated_at, dirty: (this.versions[key] || 0) !== version };
    this.saveMeta(meta);
    return true;
  }

  async flush() {
    if (!this.canSync()) return;
    if (this.flushing) { this.flushAgain = true; return; }
    if (!this.online) { this.setStatus('offline'); return; }
    this.flushing = true;
    try {
      // Re-check after each pass: anything edited while pushing is still dirty.
      for (let pass = 0; pass < 3; pass++) {
        this.flushAgain = false;
        const dirtyKeys = KEYS.filter((k) => this.getMeta()[k] && this.getMeta()[k].dirty);
        if (dirtyKeys.length === 0) break;
        this.setStatus('syncing');
        for (const key of dirtyKeys) {
          if (!(await this.push(key))) return; // status already set by push
        }
      }
      const stillDirty = KEYS.some((k) => this.getMeta()[k] && this.getMeta()[k].dirty);
      this.setStatus(stillDirty ? 'syncing' : 'synced');
    } catch (err) {
      this.fail(err);
    } finally {
      this.flushing = false;
    }
  }

  // ---- pull ----

  async pullAll() {
    if (!this.canSync()) return;
    if (!this.online) { this.setStatus('offline'); return; }
    this.setStatus('syncing');
    try {
      const { data, error } = await this.client
        .from('user_data')
        .select('key, value, updated_at')
        .eq('user_id', this.userId);
      if (error) { this.fail(error); return; }

      const rows = new Map((data || []).map((r) => [r.key, r]));
      const meta = this.getMeta();

      KEYS.forEach((key) => {
        const cfg = SYNC_KEYS[key];
        const row = rows.get(key);
        const m = meta[key] || {};
        const local = this.readLocal(key);

        if (!row) {
          // Nothing in the cloud yet: upload what we have.
          if (local !== null) meta[key] = { ...m, dirty: true };
          return;
        }
        if (!cfg.valid(row.value)) return; // ignore a malformed remote value
        if (row.updated_at === m.syncedAt) return; // remote unchanged

        if (local === null || (!m.dirty && m.syncedAt)) {
          // Local is clean (or empty): take the remote value as is.
          const next = cfg.fromRemote ? cfg.fromRemote(row.value, local) : row.value;
          this.writeLocal(key, next);
          this.emitApplied(key, next);
          this.setBase(key, row.value);
          meta[key] = { syncedAt: row.updated_at, dirty: false };
        } else {
          // First sync on this computer, or edited on two computers: merge.
          const merged = cfg.merge(local, row.value, this.baseSet(key));
          if (JSON.stringify(merged) !== JSON.stringify(local)) {
            this.writeLocal(key, merged);
            this.emitApplied(key, merged);
          }
          this.setBase(key, row.value); // the newest cloud state we have seen
          meta[key] = { syncedAt: row.updated_at, dirty: true };
        }
      });

      this.saveMeta(meta);
      await this.flush();
      if (!KEYS.some((k) => this.getMeta()[k] && this.getMeta()[k].dirty) && this.status === 'syncing') {
        this.setStatus('synced');
      }
    } catch (err) {
      this.fail(err);
    }
  }
}

const syncService = new SyncService();
export default syncService;
